import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { MessageSeverity } from "primereact/api";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import { translationKeys } from "../../../../models/app-translation-keys";
import { useSystemPromptService } from "../../../../services/ServiceHooks/systemPromptHook";
import { useStore } from "../../../../stores/StoreContext";
import { InfoBox } from "../../../Shared/InfoBox/InfoBox";

import "./SystemPromptsTab.scss";

const SystemPromptsTabRaw: FunctionComponent = () => {
  const { t } = useTranslation();
  const { toastStore, globalErrorsStore, userStore } = useStore();
  const systemPromptService = useSystemPromptService();

  const tk = translationKeys.adminArea.systemPrompts;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [text, setText] = useState("");
  const [originalText, setOriginalText] = useState("");

  const hasChanges = text !== originalText;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const item = await systemPromptService.getSystemPrompt();
        const value = item?.systemPrompt ?? "";
        setText(value);
        setOriginalText(value);
      } catch (err) {
        console.error("Failed to load system prompt", err);
        globalErrorsStore.addErrorIfNotExists(t(tk.loadFailed));
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      globalErrorsStore.removeError(t(tk.loadFailed));
      globalErrorsStore.removeError(t(tk.saveFailed));
    };
  }, [userStore.currentLanguage]);

  const onSave = async () => {
    if (saving) {
      return;
    }

    setSaving(true);
    try {
      if (!text.trim()) {
        await systemPromptService.deleteSystemPrompt();
        setText("");
        setOriginalText("");
        globalErrorsStore.removeError(t(tk.saveFailed));
        toastStore.addToast({
          severity: MessageSeverity.SUCCESS,
          summary: { translationKey: tk.deleteSuccess },
          detail: { message: "" },
        });
      } else {
        await systemPromptService.putSystemPrompt(text);
        setOriginalText(text);
        globalErrorsStore.removeError(t(tk.saveFailed));
        toastStore.addToast({
          severity: MessageSeverity.SUCCESS,
          summary: { translationKey: tk.saveSuccess },
          detail: { message: "" },
        });
      }
    } catch (err) {
      console.error("Failed to save system prompt", err);
      globalErrorsStore.addErrorIfNotExists(t(tk.saveFailed));
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    setText(originalText);
  };

  return (
    <div className="disclaimer-tab">
      {loading ? (
        <div className="disclaimer-loading">
          <ProgressSpinner />
        </div>
      ) : (
        <div className="disclaimer-form">
          <div className="disclaimer-section">
            <div className="section-header">
              <h3 className="section-title">{t(tk.title)}</h3>
              <div className="section-subheader">
                <span className="section-description">{t(tk.subheader)}</span>
                <InfoBox icon={faCircleInfo} infoTranslationKey={tk.infoTooltip} />
              </div>
            </div>

            <InputTextarea
              value={text}
              onChange={e => setText(e.target.value)}
              rows={10}
              autoResize
              placeholder={t(tk.placeholder)}
            />
          </div>

          <div className="disclaimer-actions">
            <Button label={t(tk.actions.save)} icon="pi pi-check" onClick={onSave} disabled={!hasChanges || saving} />

            <Button
              label={t(tk.actions.reset)}
              icon="pi pi-refresh"
              onClick={onReset}
              className="p-button-secondary"
              disabled={!hasChanges || saving}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const SystemPromptsTab = observer(SystemPromptsTabRaw);
